import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import layoutVariations from './layoutVariations';
import { BlockType } from '../../../types/api/ElementType';
import { Namespace } from '../../../translation/namespace';
import { BlockInteractions } from '../../organisms/BlockInteractions';
import { RichTextBlock } from '../../../_blocks/RichText';
import {
  ContentWrapperContext,
  ContentWrapperContextProvider,
  useContentWrapperContext,
} from '../ContentWrapper/ContentWrapperContext';
import { ContentImageBlock } from '../../../_blocks/ContentImage/ContentImageBlock';
import { useContentWrapperDndContext } from '../ContentWrapper/ContentWrapperDndProvider';
import { BlockWithAction } from '../ContentWrapper/ContentWrapper.types';
import { ExternalContentBlock } from '../../../_lib/components/ExternalContent';
import { PdfFileDetailsBlock } from '../../../_blocks/PdfFileDetails';
import { headlineHierarchy } from '../../organisms/BlockInteractions/BlockInteractions.utils';

const LayoutGroupContext = createContext<keyof typeof layoutVariations | undefined>(undefined);
export const useLayoutGroupContext = () => useContext(LayoutGroupContext);

export type LayoutGroup = {
  blocks: (
    | BlockWithAction<RichTextBlock>
    | BlockWithAction<ContentImageBlock>
    | BlockWithAction<ExternalContentBlock>
    | BlockWithAction<PdfFileDetailsBlock>
  )[];
  groupLayout: keyof typeof layoutVariations;
  uuid: string;
  elementType: BlockType.layoutgroup;
};

/**
 * This is not really a Block, but rather a 'glue' component used in Contentwrapper.
 * it has elementType, but no embedded so it differs vastly from other blocks.
 * we can not use asBlock() here !
 */
export function LayoutGroup({ blocks, groupLayout, uuid }: LayoutGroup) {
  const contentWrapperContext = useContentWrapperContext();
  const contentWrapperDndContext = useContentWrapperDndContext();
  const { t } = useTranslation(Namespace.LayoutGroup);
  // we cannot use editModeInfo from GlobalAppContext here:
  // In FaqList we display ContentWrappers from childPages that should not be in
  // edit mode but in default mode. To distinguish between these to cases we check if
  // all the grouped blocks have an editView.
  const editModeActive = blocks?.every(
    block => block.fetchType === 'instant' && block.embedded?.views?.edit !== undefined,
  );

  const className = `layout-group${
    editModeActive
      ? ' layout-group--edit-mode'
      : ` layout-group--default-mode layout-group--${layoutVariations[groupLayout]}`
  }`;

  const getLayoutGroupBlocks = (elementType: BlockType, block: any) => {
    switch (elementType) {
      case BlockType.contentimage:
        return <ContentImageBlock key={block.uuid} {...block} />;
      case BlockType.externalcontent:
        return <ExternalContentBlock key={block.uuid} {...block} />;
      case BlockType.pdffiledetails:
        return <PdfFileDetailsBlock key={block.uuid} {...block} />;
      case BlockType.richtext:
        return <RichTextBlock key={block.uuid} {...block} />;
      default:
        return null;
    }
  };

  if (!editModeActive) {
    return (
      <div className={className}>
        <div className="layout-group__inner">
          <LayoutGroupContext.Provider value={groupLayout}>
            {blocks.map(block => getLayoutGroupBlocks(block.elementType, block))}
          </LayoutGroupContext.Provider>
        </div>
      </div>
    );
  }

  return (
    <BlockInteractions
      contentWrapperContext={contentWrapperContext}
      contentWrapperDndContext={contentWrapperDndContext}
      views={{ default: {}, edit: {} }}
      uuid={uuid}
      elementType={BlockType.layoutgroup}
      headline={{
        text: editModeActive ? t('headline') : null,
        hierarchy: headlineHierarchy(2, contentWrapperContext?.parentIndex, contentWrapperContext?.grandParentIndex),
        visible: true,
      }}
      isNestedInCollapsibleOrLayoutGroup={!!contentWrapperContext?.parentIndex}
    >
      <div className={className}>
        <div className="layout-group__inner">
          {blocks.map((block, index) => (
            // new ContentWrapperContextProvider is set up here as nested BlockInteractions are provided
            // with ContentWrapperContext to calculate proper index and Layoutgropup adds one
            // digit to this calculated index
            <ContentWrapperContextProvider
              key={block.uuid}
              {...(contentWrapperContext as ContentWrapperContext)}
              block={block}
              index={index}
              // parentIndex is the LayoutGroup Block's index inside contentWrapper
              parentIndex={contentWrapperContext?.index}
              grandParentIndex={
                contentWrapperContext?.parentIndex ? contentWrapperContext?.parentIndex : undefined
              }
            >
              <LayoutGroupContext.Provider value={groupLayout}>
                {getLayoutGroupBlocks(block.elementType, block)}
              </LayoutGroupContext.Provider>
            </ContentWrapperContextProvider>
          ))}
        </div>
      </div>
    </BlockInteractions>
  );
}
